<template>
  <BaseModal
    :model-value="modelValue"
    size="sm"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template v-if="!showSubscribeSuccessModal" #header>
      <div class="header">Подписка на вакансии</div>
    </template>

    <div v-if="showSubscribeSuccessModal" class="subscribe-success-modal">
      <img src="~/assets/images/banners/vacancies/mailbox.png" alt="Image" />
      <p class="title">Подтвердите вашу почту</p>
      <p class="description">
        На почту <span class="text-green">{{ sendTo }}</span> отправили письмо. Подтвердите адрес
        электронной почты, чтобы получать приглашения
      </p>
    </div>

    <div v-else class="subscribe-modal">
      <div class="form">
        <BaseInput
          v-model.trim="form.email"
          :error="v$.email.$errors[0]?.$message ?? ''"
          label="Электронная почта"
          size="m"
          required
        />
        <BaseInputMask
          v-model.trim="form.phone"
          :error="v$.phone.$errors[0]?.$message ?? ''"
          label="Телефон"
          mask="+7 (###) ###-##-##"
          size="m"
          required
        />
        <AgreementsConsentBlock
          v-model:required="form.isConsentGiven"
          v-model:marketing="form.marketingAccepted"
          class="consent"
          :codes="consentCodes"
          :required-error="v$.isConsentGiven.$errors[0]?.$message ?? ''"
          @change="form.documents = $event"
        />
      </div>

      <BaseButton
        class="button"
        name="Подписаться"
        size="xl"
        :disabled="v$.$invalid"
        :loading="isSubmitting"
        @click="submitForm"
      />
    </div>
  </BaseModal>
</template>

<script lang="ts" setup>
  import { storeToRefs } from 'pinia'
  import { BaseModal } from '~/components/base-ui/modal'
  import { BaseInput, BaseInputMask } from '~/components/base-ui/input'
  import { BaseButton } from '~/components/base-ui/buttons'
  import { AgreementsConsentBlock } from '~/components/agreements'
  import { useForm } from '~/composables/use-form'
  import SubscriptionVacanciesForm from '~/services/subscriptions/SubscriptionVacanciesForm'
  import { AGREEMENTS_CONSENT_CODES } from '~/shared/const/agreements'
  import { useNotificationsStore } from '~/store/notifications'
  import { useUserStore } from '~/store/user'
  import type { SubscribeInputParams } from '~/types/subscriptions'

  const consentCodes = AGREEMENTS_CONSENT_CODES

  const props = defineProps<{
    modelValue?: boolean
    subscribeParams: SubscribeInputParams
  }>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void
    (e: 'on-close' | 'success'): void
  }>()

  const { $services } = useNuxtApp()
  const { reachGoal } = useYandexMetric()
  const { trackSelfDescribingEvent } = useSnowplow()
  const notifications = useNotificationsStore()
  const { authorizedUser } = storeToRefs(useUserStore())
  const showSubscribeSuccessModal = ref(false)
  const sendTo = ref('')

  const { form, v$, submit, reset, isSubmitting } = useForm(
    () => new SubscriptionVacanciesForm(props.subscribeParams, authorizedUser.value),
    SubscriptionVacanciesForm.rules()
  )

  async function submitForm() {
    if (!form.documents.length) {
      notifications.pushError(
        'Не удалось загрузить документы согласий. Обновите страницу и попробуйте снова.'
      )
      return
    }

    reachGoal('newsletter_subscription')

    const response = await submit(() => $services.subscriptionsService.subscribe(form.toDto()))
    if (!response) return

    emit('success')
    sendTo.value = form.email
    showSubscribeSuccessModal.value = true
    trackSelfDescribingEvent('', '', '', '', sendTo.value)
    reset()
  }
</script>

<style lang="scss" scoped>
  .subscribe-modal {
    @extend %flex-column;

    gap: 20px;

    @include vp-767 {
      margin: 0 -16px;
    }

    > .form {
      @extend %flex-column;

      gap: 16px;
    }

    > .form > .consent {
      max-width: 100%;
    }

    > .button {
      align-self: center;

      @include vp-767 {
        width: 100%;
      }
    }
  }

  .subscribe-success-modal {
    @extend %flex-column;

    align-items: center;
    gap: 20px;

    @include vp-767 {
      margin: 0 -16px;
    }

    > .title {
      font-size: 20px;
      font-weight: 500;
      line-height: 28px;
    }

    > .description {
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      text-align: center;

      .text-green {
        color: $color-kiwi-50;
      }
    }
  }

  .header {
    @extend %font-20, %font-weight-500;

    padding: 16px 32px 0;

    @include vp-767 {
      padding: 24px 0 0;
      text-align: center;
    }
  }
</style>
